import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  useGetBankAccountQuery,
  useGetAccountsQuery,
  useCreateBankAccountMutation,
  useUpdateBankAccountMutation,
} from '../../../store/api/financeApi';

function BankAccountForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new' || id === undefined;
  const invalidId = id === 'undefined';
  const { data: account, isLoading } = useGetBankAccountQuery(id, { skip: isNew || invalidId });
  const { data: accounts = [] } = useGetAccountsQuery();

  const assetAccounts = accounts.filter((a) => a.type === 'asset');

  useEffect(() => {
    if (invalidId) navigate('/finance/bank-accounts', { replace: true });
  }, [invalidId, navigate]);

  const [createBankAccount] = useCreateBankAccountMutation();
  const [updateBankAccount] = useUpdateBankAccountMutation();

  const [form, setForm] = useState({
    name: '',
    accountNumber: '',
    bankName: '',
    branch: '',
    openingBalance: '',
    accountId: '',
    isActive: true,
  });

  useEffect(() => {
    if (account) {
      setForm({
        name: account.name || '',
        accountNumber: account.accountNumber || '',
        bankName: account.bankName || '',
        branch: account.branch || '',
        openingBalance: account.openingBalance != null ? String(account.openingBalance) : '',
        accountId: account.accountId || '',
        isActive: account.isActive !== false,
      });
    }
  }, [account]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      alert('Name is required');
      return;
    }
    try {
      const payload = {
        ...form,
        accountId: form.accountId || null,
        openingBalance: parseFloat(form.openingBalance) || 0,
      };

      if (isNew) {
        await createBankAccount(payload).unwrap();
        navigate('/finance/bank-accounts');
      } else {
        await updateBankAccount({
          id,
          ...payload,
        }).unwrap();
        navigate('/finance/bank-accounts');
      }
    } catch (err) {
      alert(err?.data?.message || 'Error saving');
    }
  };

  if (invalidId) return null;
  if (!isNew && isLoading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/finance/bank-accounts')} sx={{ mb: 2 }}>
        Back
      </Button>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 3 }}>
        {isNew ? 'Add Bank Account' : 'Edit Bank Account'}
      </Typography>
      <Card sx={{ maxWidth: 560 }}>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Account name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Account number"
              value={form.accountNumber}
              onChange={(e) => setForm((p) => ({ ...p, accountNumber: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Bank name"
              value={form.bankName}
              onChange={(e) => setForm((p) => ({ ...p, bankName: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Branch"
              value={form.branch}
              onChange={(e) => setForm((p) => ({ ...p, branch: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              select
              label="Linked GL Account (Optional)"
              value={form.accountId}
              onChange={(e) => setForm((p) => ({ ...p, accountId: e.target.value }))}
              sx={{ mb: 2 }}
              helperText="Link to an existing Chart of Accounts asset or leave empty to auto-create"
            >
              <MenuItem value="">Auto-create New Ledger Account (Recommended)</MenuItem>
              {assetAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.code} – {a.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: 0.01 }}
              label="Opening balance"
              value={form.openingBalance}
              onChange={(e) => setForm((p) => ({ ...p, openingBalance: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
              }
              label="Active"
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button type="submit" variant="contained">
                {isNew ? 'Create' : 'Save'}
              </Button>
              <Button onClick={() => navigate('/finance/bank-accounts')}>Cancel</Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default BankAccountForm;

